import { useCallback, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button, Flex, Text, TextField, Theme } from "@radix-ui/themes";
import { FiChevronLeft, FiMail, FiX } from "react-icons/fi";
import { useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession } from "@aws-amplify/auth";
import { toast } from "react-toastify";
import OtcCodeInput from "@/components/ui/form/OtcCodeInput";
import {
  sendUserEmailVerification,
  verifyUserEmailAttribute,
} from "@/lib/auth";
import {
  useLoggedInUser,
  useLogout,
} from "@/components/providers/ApiProvider/hooks";
import {
  authControllerUpdateEmailAddress,
  getAuthControllerGetCurrentUserQueryKey,
} from "@/lib/lutely-api/gen/auth/auth";
import {
  emailsMatch,
  getCodeRequestCooldownMs,
  saveCodeRequestTimestamp,
} from "@/lib/storage-utils";

/**
 * Blocking email-verification gate. Typing the code in the session that
 * requested it is the point: Cognito applies a verification code to whichever
 * access token presents it, so an emailed link opened while signed in as
 * someone else verifies that other account instead.
 */
export default function EmailVerificationGate() {
  const { data: user } = useLoggedInUser();
  const queryClient = useQueryClient();
  const logoutUser = useLogout();

  const [isVerifying, setIsVerifying] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  // Close without waiting for /auth/me to come back.
  const [justVerified, setJustVerified] = useState(false);

  const [view, setView] = useState<"code" | "change-email">("code");
  const [newEmail, setNewEmail] = useState("");
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const email = user?.email ?? "";

  const handleChangeEmail = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const next = newEmail.trim();

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
        setEmailError("Enter a valid email address.");
        return;
      }
      if (emailsMatch(next, email)) {
        setEmailError("That's already the address on your account.");
        return;
      }

      setIsSavingEmail(true);
      setEmailError(null);

      // Renames the existing Cognito user and DB row in place — no second
      // account is created and the old address stops existing.
      try {
        await authControllerUpdateEmailAddress({ email: next });
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        console.error("Failed to change email address:", err);
        setEmailError(
          status === 409
            ? "An account already exists with that email."
            : "We couldn't update your email. Please try again.",
        );
        setIsSavingEmail(false);
        return;
      }

      // The address already moved, so show it even if the steps below fail.
      setNewEmail("");
      setCodeError(false);
      setErrorMessage(null);
      setView("code");

      try {
        await fetchAuthSession({ forceRefresh: true });
      } catch (err) {
        console.error("Could not refresh the session after email change:", err);
      }

      await queryClient.invalidateQueries({
        queryKey: getAuthControllerGetCurrentUserQueryKey(),
      });

      try {
        await sendUserEmailVerification();
        saveCodeRequestTimestamp(next);
        toast.success(`Verification code sent to ${next}`);
      } catch (err) {
        console.error("Email updated but sending the code failed:", err);
        setErrorMessage(
          "Your email was updated, but we couldn't send the code. Tap Resend email.",
        );
      } finally {
        setIsSavingEmail(false);
      }
    },
    [newEmail, email, queryClient],
  );

  const handleComplete = useCallback(
    async (code: string) => {
      setIsVerifying(true);
      setCodeError(false);
      setErrorMessage(null);

      try {
        await verifyUserEmailAttribute(code);
      } catch (err) {
        const name = (err as { name?: string }).name;
        console.error("Email verification failed:", err);
        setCodeError(true);
        if (name === "ExpiredCodeException") {
          setErrorMessage(
            "That code has expired or been replaced. Send a new one below.",
          );
        } else if (name === "CodeMismatchException") {
          setErrorMessage("That code isn't right. Check it and try again.");
        } else if (name === "LimitExceededException") {
          setErrorMessage("Too many attempts. Wait a few minutes, then retry.");
        } else {
          setErrorMessage("Something went wrong. Please try again.");
        }
        setIsVerifying(false);
        return;
      }

      // Verified. The rest is refresh work and must not read as a bad code.
      setJustVerified(true);
      toast.success("Your email has been verified.");

      try {
        // The gate reads the ID token's email_verified claim, so re-mint it.
        await fetchAuthSession({ forceRefresh: true });
      } catch (err) {
        console.error("Verified, but refreshing the session failed:", err);
      }

      await queryClient.invalidateQueries({
        queryKey: getAuthControllerGetCurrentUserQueryKey(),
      });
      setIsVerifying(false);
    },
    [queryClient],
  );

  const handleResend = useCallback(async () => {
    if (!email || isResending) return;

    const cooldownMs = getCodeRequestCooldownMs(email);
    if (cooldownMs > 0) {
      const minutes = Math.ceil(cooldownMs / 60_000);
      toast.info(
        `Please wait ${minutes} minute${minutes === 1 ? "" : "s"} before requesting another code`,
      );
      return;
    }

    setIsResending(true);
    setCodeError(false);
    setErrorMessage(null);
    try {
      await sendUserEmailVerification();
      saveCodeRequestTimestamp(email);
      toast.success(`New code sent to ${email}`);
    } catch (err) {
      const name = (err as { name?: string }).name;
      console.error("Failed to resend verification code:", err);
      // Cognito rate-limits sends per user. That ceiling is the real limit —
      // the cooldown above is only a local courtesy
      setErrorMessage(
        name === "LimitExceededException" || name === "TooManyRequestsException"
          ? "Too many requests. Wait a few minutes before asking for another code."
          : "Couldn't send the email. Please try again.",
      );
    } finally {
      setIsResending(false);
    }
  }, [email, isResending]);

  if (justVerified) return null;

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-[1100]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] max-w-[600px] bg-white rounded-[12px] shadow-[0px_14px_20px_-1px_rgba(5,12,43,0.14)] z-[1100] outline-none"
          // Fully blocking: no Esc, no outside click. Signing out is the exit.
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          data-testid="email-verification-gate"
        >
          <Theme>
            <Flex
              direction="column"
              align="center"
              className="max-h-[85vh] overflow-y-auto px-6 py-8 sm:px-10 text-center"
              style={{ maxHeight: "85dvh" }}
            >
              <button
                type="button"
                onClick={() => logoutUser()}
                aria-label="Sign out"
                data-testid="email-verification-gate-signout"
                className="absolute right-4 top-4 text-blue-900 hover:text-blue-600 cursor-pointer"
              >
                <FiX className="w-5 h-5" />
              </button>

              {view === "change-email" && (
                <button
                  type="button"
                  onClick={() => {
                    setView("code");
                    setEmailError(null);
                  }}
                  aria-label="Go back"
                  data-testid="email-verification-gate-back"
                  className="absolute left-4 top-4 text-blue-900 hover:text-blue-600 cursor-pointer"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
              )}

              <Dialog.Title asChild>
                <h1 className="font-figtree font-bold text-blue-900 text-[28px] leading-[1.1] tracking-[-0.28px] sm:text-[40px] sm:tracking-[-0.4px]">
                  {view === "code"
                    ? "Verify your email"
                    : "Use a different email"}
                </h1>
              </Dialog.Title>

              <Flex
                align="center"
                justify="center"
                className="my-6 h-[88px] w-[88px] rounded-full bg-blue-50"
              >
                <FiMail className="h-10 w-10 text-blue-600" />
              </Flex>

              {view === "code" ? (
                <>
                  <Dialog.Description asChild>
                    <Text className="text-[16px] text-text-secondary">
                      We sent a verification code to
                      <br />
                      <strong className="text-black">{email}</strong>
                    </Text>
                  </Dialog.Description>

                  <Text className="mt-1 mb-6 text-[16px] text-text-secondary">
                    Enter it below to continue.
                  </Text>

                  <OtcCodeInput
                    onComplete={handleComplete}
                    error={codeError}
                    errorMessage={errorMessage ?? undefined}
                    disabled={isVerifying}
                  />

                  {errorMessage && (
                    <Text
                      className="mt-3 text-[14px] text-error-bold"
                      data-testid="email-verification-gate-error"
                    >
                      {errorMessage}
                    </Text>
                  )}

                  <Button
                    variant="ghost"
                    disabled={isResending}
                    onClick={handleResend}
                    data-testid="email-verification-gate-resend"
                    className="mt-7 font-semibold text-[16px] text-blue-600 hover:text-blue-700 cursor-pointer"
                  >
                    {isResending ? "Sending..." : "Resend email"}
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => setView("change-email")}
                    data-testid="email-verification-gate-change-email"
                    className="mt-4 text-[16px] text-text-secondary hover:text-black cursor-pointer"
                  >
                    Use a different email
                  </Button>

                  <Text className="mt-7 text-[13px] text-text-secondary">
                    Didn&apos;t get it? Check your spam or promotions folder.
                  </Text>
                </>
              ) : (
                <>
                  <Dialog.Description asChild>
                    <Text className="mb-6 text-[16px] text-text-secondary">
                      We&apos;ll send a new code to this address. It replaces{" "}
                      <strong className="text-black">{email}</strong> on your
                      account.
                    </Text>
                  </Dialog.Description>

                  <form
                    onSubmit={handleChangeEmail}
                    className="w-full max-w-[360px]"
                  >
                    <Flex direction="column" gap="3">
                      <TextField.Root
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoFocus
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        inputMode="email"
                        size="3"
                        data-testid="email-verification-gate-email-input"
                      />

                      {emailError && (
                        <Text
                          className="text-left text-[14px] text-error-bold"
                          data-testid="email-verification-gate-email-error"
                        >
                          {emailError}
                        </Text>
                      )}

                      <Button
                        type="submit"
                        radius="full"
                        loading={isSavingEmail}
                        className="h-[40px] w-full cursor-pointer"
                        data-testid="email-verification-gate-email-submit"
                      >
                        Send code
                      </Button>
                    </Flex>
                  </form>
                </>
              )}
            </Flex>
          </Theme>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
